'use client';

import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import { GrainTexture } from './GrainTexture';

const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

export interface AllysComingSoonProps {
  /** Site/Brand name */
  brandName: string;
  
  /** Logo URL */
  logo?: string | undefined;
  
  /** Custom heading (default: "Coming Soon") */
  heading?: string | undefined;
  
  /** Custom message/description */
  message?: string | undefined;
  
  /** Launch date for countdown (ISO 8601 format) */
  launchDate?: string | undefined;
  
  /** Whether to show countdown timer */
  showCountdown?: boolean | undefined;
  
  /** Contact email */
  contactEmail?: string | undefined;
  
  /** Contact phone */
  contactPhone?: string | undefined;
  
  /** Whether to show contact info section */
  showContactInfo?: boolean | undefined;
  
  /** Social media links */
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    twitter?: string;
    youtube?: string;
  } | undefined;
  
  /** Whether to show social links */
  showSocialLinks?: boolean | undefined;
  
  /** Whether to show newsletter signup */
  showNewsletter?: boolean | undefined;
  
  /** Newsletter submit handler */
  onNewsletterSubmit?: ((email: string) => Promise<void>) | undefined;
  
  /** Background image URL */
  backgroundImage?: string | undefined;
  
  /** Whether password protection is enabled */
  hasPasswordProtection?: boolean | undefined;
  
  /** Organisation ID for password verification */
  organisationId?: string | undefined;
  
  /** Current locale */
  locale?: string | undefined;
  
  /** Translation messages */
  messages?: Record<string, unknown> | undefined;
}

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * Calculate time remaining until target date
 */
function calculateTimeRemaining(targetDate: string): CountdownTime {
  const now = new Date().getTime();
  const target = new Date(targetDate).getTime();
  const difference = target - now;

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((difference % (1000 * 60)) / 1000),
  };
}

/**
 * Allys Theme Coming Soon Component
 * 
 * An elegant, sophisticated coming soon page styled to match the Allys theme.
 * Features cinematic design with grain texture, smooth animations, and
 * the distinctive Allys color palette.
 */
export function AllysComingSoon({
  brandName,
  logo,
  heading,
  message,
  launchDate,
  showCountdown = true,
  contactEmail,
  contactPhone,
  showContactInfo = false,
  socialLinks,
  showSocialLinks = false,
  showNewsletter = false,
  onNewsletterSubmit,
  backgroundImage,
  hasPasswordProtection = false,
  organisationId,
  locale: _locale,
  messages,
}: AllysComingSoonProps) {
  const csMsg = messages?.comingSoon as Record<string, unknown> | undefined;
  const countdownMsg = csMsg?.countdown as Record<string, unknown> | undefined;
  const newsletterMsg = csMsg?.newsletter as Record<string, unknown> | undefined;
  const passwordMsg = csMsg?.password as Record<string, unknown> | undefined;
  const passwordErrorMsg = passwordMsg?.error as Record<string, unknown> | undefined;
  const footerMsg = csMsg?.footer as Record<string, unknown> | undefined;
  const contactMsg = csMsg?.contact as Record<string, unknown> | undefined;

  const displayHeading = heading || safeGetString(csMsg?.defaultHeading, 'Coming Soon');
  const displayMessage = message || safeGetString(csMsg?.defaultMessage, "We're crafting something extraordinary. Our new experience is on the way.");

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [timeRemaining, setTimeRemaining] = useState<CountdownTime>(() =>
    launchDate ? calculateTimeRemaining(launchDate) : { days: 0, hours: 0, minutes: 0, seconds: 0 }
  );
  const [mounted, setMounted] = useState(false);

  // Countdown timer
  useEffect(() => {
    setMounted(true);
    if (!launchDate || !showCountdown) return;

    const updateCountdown = () => {
      setTimeRemaining(calculateTimeRemaining(launchDate));
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [launchDate, showCountdown]);

  const handlePasswordSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!organisationId || !password) return;

      setPasswordError(null);
      setIsVerifying(true);

      try {
        const response = await fetch('/api/coming-soon/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ organisationId, password }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          // Reload to show the actual site
          window.location.reload();
        } else {
          setPasswordError(data.error || safeGetString(passwordErrorMsg?.incorrect, 'Incorrect password'));
        }
      } catch {
        setPasswordError(safeGetString(passwordErrorMsg?.generic, 'An error occurred. Please try again.'));
      } finally {
        setIsVerifying(false);
      }
    },
    [organisationId, password]
  );

  const handleNewsletterSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!onNewsletterSubmit || !newsletterEmail) return;

      setNewsletterStatus('loading');
      try {
        await onNewsletterSubmit(newsletterEmail);
        setNewsletterStatus('success');
        setNewsletterEmail('');
      } catch {
        setNewsletterStatus('error');
      }
    },
    [newsletterEmail, onNewsletterSubmit]
  );

  // Default background image for Allys theme
  const bgImage = backgroundImage || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&h=1080&fit=crop';

  return (
    <section className="relative min-h-screen w-full overflow-hidden bg-[var(--color-background,#243462)]">
      {/* Background Image with Cinematic Overlay */}
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.div
            key="background"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="absolute inset-0"
          >
            <Image
              src={bgImage}
              alt=""
              fill
              className="object-cover"
              priority
              sizes="100vw"
            />
            {/* Allys-style gradient overlays */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#243462]/80 via-[#040813]/60 to-[#040813]/90" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#040813]/80 via-transparent to-transparent" />
            <GrainTexture />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Additional vignette effect */}
      <div 
        className="absolute inset-0 pointer-events-none z-10" 
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0,0,0,0.4) 100%)'
        }} 
      />

      {/* Content */}
      <div className="relative z-20 min-h-screen flex flex-col items-center justify-center px-6 sm:px-8 lg:px-16 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="text-center max-w-3xl mx-auto"
        >
          {/* Logo */}
          {logo && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mb-8 lg:mb-12"
            >
              <Image
                src={logo}
                alt={brandName}
                width={200}
                height={70}
                className="mx-auto h-14 md:h-20 w-auto object-contain"
                style={{ filter: 'brightness(0) invert(1) drop-shadow(0 2px 20px rgba(0,0,0,0.5))' }}
              />
            </motion.div>
          )}

          {/* Brand Name (if no logo) */}
          {!logo && (
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-xl md:text-2xl font-bold text-white/80 mb-6 tracking-[0.2em] uppercase font-inter"
              style={{
                textShadow: '0 2px 20px rgba(0,0,0,0.5)',
              }}
            >
              {brandName}
            </motion.h2>
          )}

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6 lg:mb-8 tracking-[-0.04em] leading-[0.95] font-inter"
            style={{
              textShadow: '0 4px 60px rgba(0,0,0,0.5), 0 2px 30px rgba(0,0,0,0.4)',
            }}
          >
            {displayHeading}
          </motion.h1>

          {/* Message */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="text-lg md:text-xl lg:text-2xl text-white/80 mb-10 lg:mb-14 max-w-2xl mx-auto leading-relaxed font-inter"
            style={{
              textShadow: '0 2px 30px rgba(0,0,0,0.5)',
            }}
          >
            {displayMessage}
          </motion.p>

          {/* Countdown */}
          {showCountdown && launchDate && mounted && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.8 }}
              className="mb-12 lg:mb-16"
            >
              <div className="grid grid-cols-4 gap-4 md:gap-6 max-w-lg mx-auto">
                {[
                  { value: timeRemaining.days, label: safeGetString(countdownMsg?.days, 'Days') },
                  { value: timeRemaining.hours, label: safeGetString(countdownMsg?.hours, 'Hours') },
                  { value: timeRemaining.minutes, label: safeGetString(countdownMsg?.minutes, 'Minutes') },
                  { value: timeRemaining.seconds, label: safeGetString(countdownMsg?.seconds, 'Seconds') },
                ].map(({ value, label }) => (
                  <div key={label} className="text-center">
                    <div
                      className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tabular-nums mb-2 font-inter"
                      style={{
                        textShadow: '0 4px 40px rgba(0,0,0,0.5)',
                      }}
                    >
                      {value.toString().padStart(2, '0')}
                    </div>
                    <div
                      className="text-xs md:text-sm uppercase tracking-[0.2em] text-white/50 font-inter"
                    >
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Newsletter Signup */}
          {showNewsletter && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1 }}
              className="mb-12 max-w-md mx-auto"
            >
              <p className="text-white/60 text-sm mb-4 tracking-wide">
                {safeGetString(newsletterMsg?.title, 'Get notified when we launch')}
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder={safeGetString(newsletterMsg?.placeholder, 'Enter your email')}
                  required
                  className="flex-1 px-5 py-3.5 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#BBA339]/50 focus:border-[#BBA339] transition-all font-inter"
                />
                <button
                  type="submit"
                  disabled={newsletterStatus === 'loading'}
                  className="px-6 py-3.5 bg-[#BBA339] text-[#040813] font-semibold rounded-lg hover:bg-[#E4CE4A] transition-all disabled:opacity-50 font-inter"
                >
                  {newsletterStatus === 'loading' ? '...' : safeGetString(newsletterMsg?.button, 'Notify')}
                </button>
              </form>
              {newsletterStatus === 'success' && (
                <p className="text-[#BBA339] text-sm mt-3">{safeGetString(newsletterMsg?.success, "Thank you! We'll notify you when we launch.")}</p>
              )}
              {newsletterStatus === 'error' && (
                <p className="text-red-400 text-sm mt-3">{safeGetString(newsletterMsg?.error, 'Something went wrong. Please try again.')}</p>
              )}
            </motion.div>
          )}

          {/* Contact Info */}
          {showContactInfo && (contactEmail || contactPhone) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.1 }}
              className="mb-10 space-y-2"
            >
              <p className="text-white/50 text-sm tracking-wide mb-4">{safeGetString(contactMsg?.title, 'Contact Us')}</p>
              {contactEmail && (
                <a
                  href={`mailto:${contactEmail}`}
                  className="block text-white/80 hover:text-[#BBA339] transition-colors font-inter"
                >
                  {contactEmail}
                </a>
              )}
              {contactPhone && (
                <a
                  href={`tel:${contactPhone}`}
                  className="block text-white/80 hover:text-[#BBA339] transition-colors font-inter"
                >
                  {contactPhone}
                </a>
              )}
            </motion.div>
          )}

          {/* Social Links */}
          {showSocialLinks && socialLinks && Object.keys(socialLinks).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.2 }}
              className="mb-10"
            >
              <div className="flex items-center justify-center gap-4">
                {socialLinks.facebook && (
                  <SocialLink href={socialLinks.facebook} label="Facebook">
                    <FacebookIcon />
                  </SocialLink>
                )}
                {socialLinks.instagram && (
                  <SocialLink href={socialLinks.instagram} label="Instagram">
                    <InstagramIcon />
                  </SocialLink>
                )}
                {socialLinks.linkedin && (
                  <SocialLink href={socialLinks.linkedin} label="LinkedIn">
                    <LinkedInIcon />
                  </SocialLink>
                )}
                {socialLinks.twitter && (
                  <SocialLink href={socialLinks.twitter} label="Twitter">
                    <TwitterIcon />
                  </SocialLink>
                )}
                {socialLinks.youtube && (
                  <SocialLink href={socialLinks.youtube} label="YouTube">
                    <YouTubeIcon />
                  </SocialLink>
                )}
              </div>
            </motion.div>
          )}

          {/* Password Protection */}
          {hasPasswordProtection && organisationId && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.3 }}
              className="mt-8"
            >
              {showPasswordForm ? (
                <div className="max-w-sm mx-auto">
                  <form onSubmit={handlePasswordSubmit} className="space-y-4">
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={safeGetString(passwordMsg?.placeholder, 'Enter access password')}
                      required
                      autoComplete="current-password"
                      className="w-full px-5 py-3.5 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#BBA339]/50 focus:border-[#BBA339] transition-all font-inter"
                    />
                    {passwordError && (
                      <p className="text-red-400 text-sm">{passwordError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={isVerifying || !password}
                      className="w-full px-6 py-3.5 bg-[#BBA339] text-[#040813] font-semibold rounded-lg hover:bg-[#E4CE4A] transition-all disabled:opacity-50 flex items-center justify-center gap-2 font-inter"
                    >
                      {isVerifying ? (
                        <>
                          <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          {safeGetString(passwordMsg?.verifying, 'Verifying...')}
                        </>
                      ) : (
                        safeGetString(passwordMsg?.button, 'Access Site')
                      )}
                    </button>
                  </form>
                  <button
                    onClick={() => setShowPasswordForm(false)}
                    className="mt-4 text-white/50 text-sm hover:text-white transition-colors"
                  >
                    {safeGetString(passwordMsg?.cancel, 'Cancel')}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowPasswordForm(true)}
                  className="text-white/50 text-sm hover:text-[#BBA339] transition-colors underline underline-offset-4 decoration-white/30 hover:decoration-[#BBA339] font-inter"
                >
                  {safeGetString(passwordMsg?.prompt, 'Have access password?')}
                </button>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-6 left-0 right-0 text-center z-20">
        <p 
          className="text-white/30 text-sm font-inter"
        >
          {safeGetString(footerMsg?.copyright, '© {year} {brandName}. All rights reserved.')
            .replace('{year}', new Date().getFullYear().toString())
            .replace('{brandName}', brandName)}
        </p>
      </div>
    </section>
  );
}

// Social Link Component
function SocialLink({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="w-11 h-11 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-md border border-white/10 hover:bg-[#BBA339]/20 hover:border-[#BBA339]/30 text-white/70 hover:text-[#BBA339] transition-all"
    >
      {children}
    </a>
  );
}

// Social Icons (same as core component)
function FacebookIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function TwitterIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function YouTubeIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}
